import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { SectionLabel } from '@/components/ui/section-label';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTranslation } from '@/hooks/useTranslation';
import { useReferenceAlias } from '@/hooks/useReferenceAlias';
import { useState } from 'react';
import { ChevronDown, Filter } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface ToolListFiltersProps {
  search: string;
  references: string[];
  allReferences: string[];
  health: string | null;
  healthOptions: { value: string; labelKey: string }[];
  onSearchChange: (value: string) => void;
  onReferenceChange: (values: string[]) => void;
  onHealthChange: (value: string | null) => void;
  onReset?: () => void;
}

export function ToolListFilters({
  search,
  references,
  allReferences,
  health,
  healthOptions,
  onSearchChange,
  onReferenceChange,
  onHealthChange,
  onReset,
}: ToolListFiltersProps) {
  const { t } = useTranslation();
  const { resolve } = useReferenceAlias();
  const [isOpen, setIsOpen] = useState(false);

  const activeCount = (search ? 1 : 0) + references.length + (health ? 1 : 0);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card padding="none">
        {/* Compact header bar */}
        <div className="flex items-center justify-between px-4 py-2">
          <CollapsibleTrigger className="group flex flex-1 items-center gap-2 rounded-md text-left transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50">
            <SectionLabel
              icon={Filter}
              className="transition group-hover:border-primary/40 group-hover:text-foreground"
            >
              {t('toolView.list.filters.title')}
            </SectionLabel>
            {activeCount > 0 && !isOpen && (
              <Badge tone="primary">
                {t('toolView.list.filters.activeCount', { count: activeCount })}
              </Badge>
            )}
            <ChevronDown
              className={`h-4 w-4 text-muted-foreground transition-transform duration-200 group-hover:text-foreground ${isOpen ? 'rotate-180' : ''}`}
            />
          </CollapsibleTrigger>
          {onReset && activeCount > 0 && (
            <Button variant="ghost" size="sm" className="ml-2" onClick={onReset}>
              {t('toolView.list.filters.reset')}
            </Button>
          )}
        </div>

        {/* Expanded fields — 3-col grid on lg+ */}
        <CollapsibleContent>
          <div className="grid grid-cols-1 gap-4 border-t border-border/60 px-4 py-4 lg:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="tool-search" className="text-sm font-medium">
                {t('toolView.list.filters.search.label')}
              </Label>
              <Input
                id="tool-search"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={t('toolView.list.filters.search.placeholder')}
                className="h-9 focus-visible:ring-primary/30"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="references" className="text-sm font-medium">
                {t('toolView.list.filters.reference.label')}
              </Label>
              <Select
                value={references[0] || '__all__'}
                onValueChange={(value) => onReferenceChange(value === '__all__' ? [] : [value])}
              >
                <SelectTrigger id="references" className="w-full">
                  <SelectValue placeholder={t('toolView.list.filters.reference.all')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">
                    {t('toolView.list.filters.reference.all')}
                  </SelectItem>
                  {allReferences.map((ref) => (
                    <SelectItem key={ref} value={ref}>
                      {resolve(ref).label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="health" className="text-sm font-medium">
                {t('toolView.list.filters.health.label')}
              </Label>
              <Select
                value={health || '__any__'}
                onValueChange={(value) => onHealthChange(value === '__any__' ? null : value)}
              >
                <SelectTrigger id="health" className="w-full">
                  <SelectValue placeholder={t('toolView.list.filters.health.any')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">{t('toolView.list.filters.health.any')}</SelectItem>
                  {healthOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {t(opt.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
